// ============================================================
// Helpers de rendu isométrique partagés (solo + coop).
// Extrait de IsometricMap.jsx pour réutilisation par CoopMap.jsx.
// ============================================================

// === Constantes de salle (style Binding of Isaac) ===
export const ROOM_W = 12; // tiles de large (intérieur, entre murs)
export const ROOM_H = 8; // tiles de haut
export const TILE_W = 64;
export const TILE_H = 32;
export const PLAYER_SPEED = 4.5; // tiles/seconde
export const DOOR_RADIUS = 0.9;
export const INTERACT_RADIUS = 1.3;

export const NODE_SPRITE = {
  combat: "/sprites/enemy_small.png",
  elite: "/sprites/enemy_elite.png",
  boss: "/sprites/enemy_boss.png",
  chest: "/sprites/chest.png",
  shop: "/sprites/shop_npc.png",
};

export const NODE_LABEL = {
  combat: "Combat",
  elite: "Élite",
  chest: "Coffre",
  sanctuary: "Sanctuaire",
  shop: "Boutique",
  boss: "BOSS",
};

// Projection iso : coords intérieures de salle -> écran
export const isoProject = (wx, wy, canvasW, canvasH) => {
  const cx = ROOM_W / 2;
  const cy = ROOM_H / 2;
  return {
    x: (wx - cx - (wy - cy)) * (TILE_W / 2) + canvasW / 2,
    y: (wx - cx + (wy - cy)) * (TILE_H / 2) + canvasH / 2,
  };
};

// Positions des portes sur les murs
export const DOOR_POS = {
  N: { wx: ROOM_W / 2, wy: 0, side: "N" },
  S: { wx: ROOM_W / 2, wy: ROOM_H, side: "S" },
  W: { wx: 0, wy: ROOM_H / 2, side: "W" },
  E: { wx: ROOM_W, wy: ROOM_H / 2, side: "E" },
};

export const floorPaletteFor = (roomType, theme) => {
  if (roomType === "boss") return { dark: "#4c1d2e", light: "#5b1f33", line: "rgba(0,0,0,0.4)" };
  if (roomType === "sanctuary") return { dark: "#1e3a8a", light: "#1e40af", line: "rgba(255,255,255,0.06)" };
  if (roomType === "chest") return { dark: "#3f3014", light: "#52401d", line: "rgba(0,0,0,0.4)" };
  if (roomType === "shop") return { dark: "#3b0d4a", light: "#4c1456", line: "rgba(0,0,0,0.4)" };
  if (roomType === "elite") return { dark: "#2b1f3b", light: "#3a2748", line: "rgba(0,0,0,0.4)" };
  return { dark: "#1e293b", light: "#27384f", line: "rgba(0,0,0,0.4)" };
};

// Couleurs des murs par biome — dérivées des décors de combat (2 faces : NE plus claire, NW plus sombre).
const BIOME_WALLS = {
  forest:    { back: "#3a6e58", side: "#2c5644" },
  cavern:    { back: "#3a4170", side: "#2c3354" },
  ruins:     { back: "#6a5331", side: "#4f3e22" },
  glacial:   { back: "#234e7a", side: "#1a3a5c" },
  volcano:   { back: "#6b3322", side: "#4f2619" },
  celestial: { back: "#b89a5e", side: "#8f7642" },
  astral:    { back: "#342770", side: "#271d54" },
  abyss:     { back: "#2c2347", side: "#201833" },
};

export function drawWalls(ctx, W, H, themeId = "forest") {
  const tl = isoProject(0, 0, W, H);
  const tr = isoProject(ROOM_W, 0, W, H);
  const bl = isoProject(0, ROOM_H, W, H);
  const WALL_H = 56;
  const wall = BIOME_WALLS[themeId] || BIOME_WALLS.forest;

  ctx.fillStyle = wall.back;
  ctx.beginPath();
  ctx.moveTo(tl.x, tl.y);
  ctx.lineTo(tr.x, tr.y);
  ctx.lineTo(tr.x, tr.y - WALL_H);
  ctx.lineTo(tl.x, tl.y - WALL_H);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.7)";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.fillStyle = wall.side;
  ctx.beginPath();
  ctx.moveTo(tl.x, tl.y);
  ctx.lineTo(bl.x, bl.y);
  ctx.lineTo(bl.x, bl.y - WALL_H);
  ctx.lineTo(tl.x, tl.y - WALL_H);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Liseré clair sur l'arête supérieure des murs (lumière, profondeur 2.5D)
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(bl.x, bl.y - WALL_H);
  ctx.lineTo(tl.x, tl.y - WALL_H);
  ctx.lineTo(tr.x, tr.y - WALL_H);
  ctx.stroke();
}

// drawDoor : targetNode optionnel (mode coop = porte générique). isBoss seulement si fourni.
export function drawDoor(ctx, pos, targetNode, W, H, now, showLabel = false) {
  const s = isoProject(pos.wx, pos.wy, W, H);
  const isBoss = targetNode?.type === "boss";
  const pulse = 0.5 + 0.5 * Math.sin(now / (isBoss ? 180 : 300));
  if (isBoss) {
    ctx.fillStyle = `rgba(220, 38, 38, ${0.35 + 0.35 * pulse})`;
    ctx.beginPath();
    ctx.ellipse(s.x, s.y, 50 + pulse * 12, 22 + pulse * 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(254, 240, 138, ${0.25 + 0.25 * pulse})`;
    ctx.beginPath();
    ctx.ellipse(s.x, s.y, 36, 16, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillStyle = `rgba(250, 204, 21, ${0.25 + 0.25 * pulse})`;
    ctx.beginPath();
    ctx.ellipse(s.x, s.y, 32, 14, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = isBoss ? "#1f0808" : "#1b1208";
  ctx.fillRect(s.x - 14, s.y - 40, 28, 40);
  ctx.fillStyle = isBoss ? "#7c1d1d" : "#7c4a1d";
  ctx.fillRect(s.x - 10, s.y - 36, 20, 36);
  ctx.fillStyle = "#1f2937";
  for (const [dx, dy] of [[-5, -30], [5, -30], [-5, -8], [5, -8]]) {
    ctx.beginPath();
    ctx.arc(s.x + dx, s.y + dy, 1.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = isBoss ? "#dc2626" : "#fde047";
  ctx.beginPath();
  ctx.arc(s.x + 6, s.y - 18, 2.5, 0, Math.PI * 2);
  ctx.fill();
  if (isBoss) {
    for (let i = 0; i < 4; i++) {
      const t = now / 600 + (i / 4) * Math.PI * 2;
      const sx = s.x + Math.cos(t) * 22;
      const sy = s.y - 20 + Math.sin(t) * 12;
      const alpha = 0.5 + 0.5 * Math.sin(now / 200 + i);
      ctx.fillStyle = `rgba(254, 240, 138, ${alpha})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 1.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.8})`;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(sx - 3, sy); ctx.lineTo(sx + 3, sy);
      ctx.moveTo(sx, sy - 3); ctx.lineTo(sx, sy + 3);
      ctx.stroke();
    }
    const bobY = Math.sin(now / 400) * 2;
    ctx.fillStyle = "#fef3c7";
    ctx.beginPath();
    ctx.arc(s.x, s.y - 56 + bobY, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#1c1917";
    ctx.fillRect(s.x - 3, s.y - 58 + bobY, 1.8, 2.4);
    ctx.fillRect(s.x + 1.2, s.y - 58 + bobY, 1.8, 2.4);
    ctx.fillRect(s.x - 2, s.y - 53 + bobY, 4, 1.4);
  }
  if (showLabel && targetNode) {
    const label = NODE_LABEL[targetNode.type] || targetNode.type;
    ctx.fillStyle = "#fef3c7";
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "center";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 3;
    ctx.strokeText(label, s.x, s.y - 50);
    ctx.fillText(label, s.x, s.y - 50);
  }
}

export function drawDecoration(ctx, roomType, W, H, now) {
  const corners = [
    isoProject(0.5, 0.5, W, H),
    isoProject(ROOM_W - 0.5, 0.5, W, H),
    isoProject(0.5, ROOM_H - 0.5, W, H),
    isoProject(ROOM_W - 0.5, ROOM_H - 0.5, W, H),
  ];
  for (const c of corners) {
    ctx.fillStyle = "#1f2937";
    ctx.fillRect(c.x - 6, c.y - 28, 12, 28);
    const flicker = 0.5 + 0.5 * Math.sin(now / 100 + c.x);
    ctx.fillStyle = `rgba(251, 146, 60, ${0.7 + 0.3 * flicker})`;
    ctx.beginPath();
    ctx.ellipse(c.x, c.y - 32, 6 + flicker * 2, 10 + flicker * 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(254, 240, 138, ${0.5 + 0.5 * flicker})`;
    ctx.beginPath();
    ctx.ellipse(c.x, c.y - 34, 3, 5 + flicker * 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  if (roomType === "sanctuary") {
    const center = isoProject(ROOM_W / 2, ROOM_H / 2, W, H);
    ctx.strokeStyle = `rgba(168, 85, 247, ${0.3 + 0.2 * Math.sin(now / 400)})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(center.x, center.y + 4, 70, 28, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(center.x, center.y + 4, 90, 36, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (roomType === "boss") {
    for (const [rx, ry] of [[2, 2], [ROOM_W - 2, 2], [2, ROOM_H - 2], [ROOM_W - 2, ROOM_H - 2], [ROOM_W / 2, 1.5]]) {
      const s = isoProject(rx, ry, W, H);
      ctx.fillStyle = "#cbd5e1";
      ctx.beginPath();
      ctx.arc(s.x, s.y - 6, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#000";
      ctx.fillRect(s.x - 3, s.y - 7, 1.5, 2);
      ctx.fillRect(s.x + 1.5, s.y - 7, 1.5, 2);
    }
  } else if (roomType === "shop") {
    for (const [rx, ry] of [[ROOM_W / 2 - 2, ROOM_H / 2 + 0.5], [ROOM_W / 2 + 2, ROOM_H / 2 + 0.5]]) {
      const s = isoProject(rx, ry, W, H);
      ctx.fillStyle = "#7c4a1d";
      ctx.fillRect(s.x - 10, s.y - 14, 20, 14);
      ctx.fillStyle = "#a16207";
      ctx.fillRect(s.x - 10, s.y - 14, 20, 3);
      ctx.strokeStyle = "#000";
      ctx.strokeRect(s.x - 10, s.y - 14, 20, 14);
    }
  } else if (roomType === "chest") {
    for (let i = 0; i < 6; i++) {
      const rx = 2 + (i % 3) * 3;
      const ry = ROOM_H - 2 - Math.floor(i / 3);
      const s = isoProject(rx, ry, W, H);
      ctx.fillStyle = "#fde047";
      ctx.beginPath();
      ctx.arc(s.x, s.y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (roomType === "elite" || roomType === "combat") {
    for (const [rx, ry] of [[1.5, 1.5], [ROOM_W - 1.5, ROOM_H - 1.5], [2.5, ROOM_H - 2.5]]) {
      const s = isoProject(rx, ry, W, H);
      ctx.fillStyle = "#475569";
      ctx.beginPath();
      ctx.ellipse(s.x, s.y, 8, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#000";
      ctx.stroke();
    }
  }
}

export function drawRotatingCrystal(ctx, cx, cy, now) {
  const t = now / 600;
  const angle = t;
  const visibleScaleX = Math.abs(Math.cos(angle));
  const pulse = 0.5 + 0.5 * Math.sin(now / 300);

  ctx.fillStyle = `rgba(168, 85, 247, ${0.15 + 0.15 * pulse})`;
  ctx.beginPath();
  ctx.arc(cx, cy + 4, 32 + pulse * 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = `rgba(216, 180, 254, ${0.3 + 0.3 * pulse})`;
  ctx.beginPath();
  ctx.arc(cx, cy + 4, 20 + pulse * 4, 0, Math.PI * 2);
  ctx.fill();

  const W = 14 * visibleScaleX + 1;
  const H_TOP = 20;
  const H_BOT = 12;
  const MID = 4;

  ctx.fillStyle = "#e9d5ff";
  ctx.beginPath();
  ctx.moveTo(cx, cy - H_TOP);
  ctx.lineTo(cx + W, cy - MID);
  ctx.lineTo(cx, cy + H_BOT);
  ctx.lineTo(cx - W, cy - MID);
  ctx.closePath();
  ctx.fill();
  const facetWidth = W * Math.max(0, Math.sin(angle));
  if (facetWidth > 0.5) {
    ctx.fillStyle = "#7c3aed";
    ctx.beginPath();
    ctx.moveTo(cx, cy - H_TOP);
    ctx.lineTo(cx + facetWidth, cy - MID);
    ctx.lineTo(cx, cy + H_BOT);
    ctx.closePath();
    ctx.fill();
  }
  const facetWidthL = W * Math.max(0, -Math.sin(angle));
  if (facetWidthL > 0.5) {
    ctx.fillStyle = "#7c3aed";
    ctx.beginPath();
    ctx.moveTo(cx, cy - H_TOP);
    ctx.lineTo(cx - facetWidthL, cy - MID);
    ctx.lineTo(cx, cy + H_BOT);
    ctx.closePath();
    ctx.fill();
  }
  ctx.strokeStyle = "#581c87";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx, cy - H_TOP);
  ctx.lineTo(cx + W, cy - MID);
  ctx.lineTo(cx, cy + H_BOT);
  ctx.lineTo(cx - W, cy - MID);
  ctx.closePath();
  ctx.stroke();

  ctx.fillStyle = `rgba(255, 255, 255, ${0.55 + 0.3 * pulse})`;
  ctx.beginPath();
  ctx.moveTo(cx - 1, cy - H_TOP + 3);
  ctx.lineTo(cx + 3, cy - MID + 1);
  ctx.lineTo(cx + 1, cy + 2);
  ctx.lineTo(cx - 2, cy - MID - 2);
  ctx.closePath();
  ctx.fill();

  for (let i = 0; i < 4; i++) {
    const ang = t * 1.5 + (i / 4) * Math.PI * 2;
    const sx = cx + Math.cos(ang) * 18;
    const sy = cy - 4 + Math.sin(ang) * 8;
    const alpha = 0.4 + 0.6 * Math.sin(now / 250 + i);
    ctx.fillStyle = `rgba(254, 240, 138, ${alpha})`;
    ctx.beginPath();
    ctx.arc(sx, sy, 1.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.7})`;
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(sx - 2.5, sy); ctx.lineTo(sx + 2.5, sy);
    ctx.moveTo(sx, sy - 2.5); ctx.lineTo(sx, sy + 2.5);
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
  ctx.beginPath();
  ctx.ellipse(cx, cy + H_BOT + 12, 14, 4, 0, 0, Math.PI * 2);
  ctx.fill();
}

export function drawInteractable(ctx, node, sprites, W, H, now) {
  const s = isoProject(ROOM_W / 2, ROOM_H / 2, W, H);

  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.beginPath();
  ctx.ellipse(s.x, s.y + 6, 26, 8, 0, 0, Math.PI * 2);
  ctx.fill();

  const pulse = 0.5 + 0.5 * Math.sin(now / 300);
  let haloColor = "rgba(250, 204, 21, ";
  if (node.type === "sanctuary") haloColor = "rgba(168, 85, 247, ";
  if (node.type === "boss") haloColor = "rgba(220, 38, 38, ";
  ctx.fillStyle = haloColor + (0.2 + 0.25 * pulse) + ")";
  ctx.beginPath();
  ctx.ellipse(s.x, s.y + 6, 40 + pulse * 8, 14 + pulse * 4, 0, 0, Math.PI * 2);
  ctx.fill();

  const SIZE = node.type === "boss" ? 130 : node.type === "elite" ? 100 : 84;
  const bob = Math.sin(now / 250) * 3;
  const spriteKey = node.type === "boss" ? "enemy_boss"
    : node.type === "elite" ? "enemy_elite"
    : node.type === "combat" ? "enemy_small"
    : node.type === "chest" ? "chest"
    : node.type === "shop" ? "shop_npc"
    : null;
  const sprite = spriteKey ? sprites[spriteKey] : null;

  if (sprite) {
    ctx.drawImage(sprite, s.x - SIZE / 2, s.y - SIZE + 8 + bob, SIZE, SIZE);
  } else if (node.type === "sanctuary") {
    drawRotatingCrystal(ctx, s.x, s.y - 30 + bob * 0.5, now);
  }

  const iconLabel = {
    sanctuary: "✨",
    chest: "📦",
    shop: "💰",
    combat: "⚔️",
    elite: "⭐",
    boss: "💀",
  }[node.type];
  if (iconLabel) {
    const iconBob = Math.sin(now / 350) * 3;
    const iconY = s.y - SIZE - 6 + iconBob;
    const iconPulse = 0.4 + 0.6 * Math.sin(now / 400);
    ctx.fillStyle = `rgba(0, 0, 0, 0.55)`;
    ctx.beginPath();
    ctx.arc(s.x, iconY, 14, 0, Math.PI * 2);
    ctx.fill();
    if (node.type === "sanctuary") ctx.fillStyle = `rgba(168, 85, 247, ${0.4 * iconPulse})`;
    else if (node.type === "shop") ctx.fillStyle = `rgba(250, 204, 21, ${0.4 * iconPulse})`;
    else if (node.type === "chest") ctx.fillStyle = `rgba(245, 158, 11, ${0.4 * iconPulse})`;
    else if (node.type === "boss") ctx.fillStyle = `rgba(220, 38, 38, ${0.5 * iconPulse})`;
    else ctx.fillStyle = `rgba(244, 63, 94, ${0.4 * iconPulse})`;
    ctx.beginPath();
    ctx.arc(s.x, iconY, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = "bold 20px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(iconLabel, s.x, iconY);
    ctx.textBaseline = "alphabetic";
  }
}

// Config d'animation par classe (couleurs, aura, queue)
export const CLASS_FX = {
  duelliste: { aura: "rgba(251, 146, 60, ", trailType: "ember", trailColors: ["#fb923c", "#f59e0b", "#dc2626"], weaponTrail: true },
  eclaireur: { aura: "rgba(125, 211, 252, ", trailType: "snow", trailColors: ["#e0f2fe", "#bae6fd", "#7dd3fc"] },
  heros: { aura: "rgba(254, 240, 138, ", trailType: "spark", trailColors: ["#fde047", "#facc15", "#fef3c7"] },
  oracle: { aura: "rgba(216, 180, 254, ", trailType: "wind", trailColors: ["#f5f3ff", "#e9d5ff", "#c4b5fd"] },
  libromancien: { aura: "rgba(168, 85, 247, ", trailType: "shadow", trailColors: ["#7c3aed", "#a855f7", "#581c87"] },
  berger: { aura: "rgba(134, 239, 172, ", trailType: "leaf", trailColors: ["#86efac", "#4ade80", "#22c55e"] },
};

export function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function drawPlayerTrail(ctx, trail, fx, W, H) {
  if (!trail || !trail.length || !fx) return;
  for (const tp of trail) {
    const alpha = Math.max(0, 1 - tp.life / tp.maxLife);
    const s = isoProject(tp.wx, tp.wy, W, H);
    const color = fx.trailColors[Math.floor(tp.seed * fx.trailColors.length)];
    const r = tp.size * (1 - tp.life / tp.maxLife * 0.4);
    if (fx.trailType === "ember") {
      ctx.fillStyle = hexToRgba(color, alpha * 0.9);
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = hexToRgba("#fde047", alpha * 0.4);
      ctx.beginPath(); ctx.arc(s.x, s.y, r * 0.5, 0, Math.PI * 2); ctx.fill();
    } else if (fx.trailType === "snow") {
      ctx.fillStyle = hexToRgba(color, alpha * 0.8);
      ctx.beginPath(); ctx.arc(s.x, s.y, r, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = hexToRgba("#ffffff", alpha * 0.6); ctx.lineWidth = 0.6;
      ctx.beginPath(); ctx.moveTo(s.x - r, s.y); ctx.lineTo(s.x + r, s.y);
      ctx.moveTo(s.x, s.y - r); ctx.lineTo(s.x, s.y + r); ctx.stroke();
    } else if (fx.trailType === "spark") {
      ctx.fillStyle = hexToRgba(color, alpha);
      ctx.beginPath(); ctx.arc(s.x, s.y, r * 0.8, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = hexToRgba("#ffffff", alpha * 0.7); ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(s.x - r * 1.5, s.y); ctx.lineTo(s.x + r * 1.5, s.y);
      ctx.moveTo(s.x, s.y - r * 1.5); ctx.lineTo(s.x, s.y + r * 1.5); ctx.stroke();
    } else if (fx.trailType === "wind") {
      ctx.strokeStyle = hexToRgba(color, alpha * 0.7); ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r * 1.2, 0, Math.PI * 1.5);
      ctx.stroke();
    } else if (fx.trailType === "shadow") {
      ctx.fillStyle = hexToRgba(color, alpha * 0.6);
      ctx.beginPath(); ctx.ellipse(s.x, s.y, r * 1.5, r * 0.8, tp.seed * Math.PI, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = hexToRgba("#1c1917", alpha * 0.4);
      ctx.beginPath(); ctx.ellipse(s.x, s.y, r, r * 0.5, 0, 0, Math.PI * 2); ctx.fill();
    } else if (fx.trailType === "leaf") {
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(tp.life * 6 + tp.seed * 10);
      ctx.fillStyle = hexToRgba(color, alpha * 0.85);
      ctx.beginPath(); ctx.ellipse(0, 0, r * 1.4, r * 0.6, 0, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }
}

// Choisit le sprite directionnel selon l'orientation. Retombe sur le sprite de face
// si une direction n'a pas d'asset dédié. `flip` = miroir horizontal (côté gauche).
function pickDirSprite(sprites, facing) {
  if (!sprites) return { img: null, flip: false };
  // Si on a passé un sprite unique (rétrocompat), on l'utilise tel quel.
  if (sprites instanceof HTMLCanvasElement || sprites instanceof Image) {
    return { img: sprites, flip: facing === "left" };
  }
  const { front, back, side } = sprites;
  if (facing === "up" && back) return { img: back, flip: false };
  if (facing === "left" && side) return { img: side, flip: true };
  if (facing === "right" && side) return { img: side, flip: false };
  // bas / défaut → face (miroir si gauche et pas de sprite de côté)
  return { img: front || side || back, flip: facing === "left" && !front };
}

export function drawPlayer(ctx, player, sprites, W, H, now, classeId, trail) {
  const s = isoProject(player.wx, player.wy, W, H);
  const fx = CLASS_FX[classeId];

  if (fx && trail && trail.length) drawPlayerTrail(ctx, trail, fx, W, H);

  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.beginPath();
  ctx.ellipse(s.x, s.y + 6, 16, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  if (fx) {
    const auraStrength = player.moving ? 0.45 : 0.22;
    const pulse = 0.5 + 0.5 * Math.sin(now / 300);
    ctx.fillStyle = fx.aura + (auraStrength * (0.6 + 0.4 * pulse)).toFixed(2) + ")";
    ctx.beginPath();
    ctx.ellipse(s.x, s.y + 2, 22 + pulse * 4, 8 + pulse * 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const { img: sprite, flip } = pickDirSprite(sprites, player.facing);
  if (sprite) {
    const SIZE = 80;
    const t = now / 90;
    const bob = player.moving ? Math.abs(Math.sin(t)) * 6 : Math.sin(now / 400) * 1.5;
    const squashY = player.moving ? 1 - Math.abs(Math.sin(t)) * 0.08 : 1;
    const tilt = player.moving ? Math.sin(t) * 0.06 : 0;
    const sway = player.moving ? Math.cos(t) * 2 : 0;
    ctx.save();
    ctx.translate(s.x + sway, s.y + 8 - SIZE * squashY + bob);
    if (flip) ctx.scale(-1, squashY);
    else ctx.scale(1, squashY);
    ctx.rotate(tilt);
    ctx.drawImage(sprite, -SIZE / 2, 0, SIZE, SIZE);
    ctx.restore();

    if (fx?.weaponTrail && player.moving) {
      const wt = now / 150;
      for (let i = 0; i < 3; i++) {
        const offset = i * 6;
        const flicker = 0.5 + 0.5 * Math.sin(wt + i);
        const sideX = (flip ? 12 : -12) + Math.sin(wt + i) * 3;
        const yOff = -30 + i * 4 + Math.cos(wt + i) * 2;
        ctx.fillStyle = `rgba(251, 146, 60, ${0.5 * flicker})`;
        ctx.beginPath();
        ctx.ellipse(s.x + sideX - offset * 0.3, s.y + yOff + bob, 4 - i, 9 - i * 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(254, 240, 138, ${0.45 * flicker})`;
        ctx.beginPath();
        ctx.ellipse(s.x + sideX - offset * 0.3, s.y + yOff + bob - 2, 2, 5 - i, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else {
    ctx.fillStyle = "#f43f5e";
    ctx.fillRect(s.x - 10, s.y - 28, 20, 28);
  }
}

// Sprite d'un allié coop (léger : pas de queue/arme). Affiche le nom + (option) état "mort".
export function drawRemotePlayer(ctx, player, sprites, W, H, now, classeId, name, dead = false) {
  const s = isoProject(player.wx, player.wy, W, H);
  const fx = CLASS_FX[classeId];

  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.beginPath();
  ctx.ellipse(s.x, s.y + 6, 15, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  if (fx && !dead) {
    const pulse = 0.5 + 0.5 * Math.sin(now / 300);
    ctx.fillStyle = fx.aura + (0.3 * (0.6 + 0.4 * pulse)).toFixed(2) + ")";
    ctx.beginPath();
    ctx.ellipse(s.x, s.y + 2, 20 + pulse * 3, 7 + pulse * 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const { img: sprite, flip } = pickDirSprite(sprites, player.facing);
  if (sprite) {
    const SIZE = 80;
    const t = now / 90;
    const bob = player.moving && !dead ? Math.abs(Math.sin(t)) * 6 : Math.sin(now / 400) * 1.5;
    const squashY = player.moving && !dead ? 1 - Math.abs(Math.sin(t)) * 0.08 : 1;
    ctx.save();
    ctx.translate(s.x, s.y + 8 - SIZE * squashY + bob);
    if (flip) ctx.scale(-1, squashY);
    else ctx.scale(1, squashY);
    ctx.globalAlpha = dead ? 0.35 : 0.92;
    if (dead) ctx.filter = "grayscale(1)";
    ctx.drawImage(sprite, -SIZE / 2, 0, SIZE, SIZE);
    ctx.filter = "none";
    ctx.globalAlpha = 1;
    ctx.restore();
  } else {
    ctx.fillStyle = dead ? "#64748b" : "#38bdf8";
    ctx.fillRect(s.x - 10, s.y - 28, 20, 28);
  }

  if (name) {
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "center";
    const label = dead ? `☠ ${name}` : name;
    const ty = s.y - 84;
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(s.x - tw / 2 - 4, ty - 11, tw + 8, 15);
    ctx.fillStyle = dead ? "#f87171" : "#7dd3fc";
    ctx.fillText(label, s.x, ty);
  }
}
